import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { Plus, Edit, Trash2, Percent } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Discounts() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [rules, setRules] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name: '', min_qty: 1, discount_percent: 0, active: true, scope: 'global', scope_value: '' });

  const fetch = () => axios.get(`${API}/admin/discounts`, { headers }).then(r => setRules(r.data)).catch(() => {});
  useEffect(() => { fetch(); }, [token]);

  const handleSave = async () => {
    if (!form.name || form.min_qty < 1 || form.discount_percent <= 0) { toast.error('Complete todos los campos correctamente'); return; }
    try {
      if (editItem) {
        await axios.put(`${API}/admin/discounts/${editItem.id}`, form, { headers });
        toast.success('Regla actualizada');
      } else {
        await axios.post(`${API}/admin/discounts`, form, { headers });
        toast.success('Regla creada');
      }
      setDialogOpen(false);
      setEditItem(null);
      setForm({ name: '', min_qty: 1, discount_percent: 0, active: true, scope: 'global', scope_value: '' });
      fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`¿Eliminar "${name}"?`)) return;
    try { await axios.delete(`${API}/admin/discounts/${id}`, { headers }); toast.success('Eliminada'); fetch(); } catch (err) { toast.error('Error'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-display font-semibold">Reglas de Descuento</h1>
        <Button onClick={() => { setEditItem(null); setForm({ name: '', min_qty: 1, discount_percent: 0, active: true, scope: 'global', scope_value: '' }); setDialogOpen(true); }} className="gap-2">
          <Plus className="h-4 w-4" /> Nueva Regla
        </Button>
      </div>

      <p className="text-sm text-muted-foreground">Define descuentos automáticos basados en la cantidad de artículos en el carrito.</p>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rules.map(r => (
          <Card key={r.id}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-md bg-secondary"><Percent className="h-4 w-4" /></div>
                  <div>
                    <h3 className="font-display font-semibold text-sm">{r.name}</h3>
                    <Badge variant={r.active ? 'secondary' : 'outline'} className="text-xs mt-1">{r.active ? 'Activa' : 'Inactiva'}</Badge>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => { setEditItem(r); setForm({ name: r.name, min_qty: r.min_qty, discount_percent: r.discount_percent, active: r.active, scope: r.scope || 'global', scope_value: r.scope_value || '' }); setDialogOpen(true); }}><Edit className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(r.id, r.name)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
              <div className="bg-secondary/50 rounded-md p-3 text-center">
                <p className="text-2xl font-display font-semibold">{r.discount_percent}%</p>
                <p className="text-xs text-muted-foreground">de descuento</p>
              </div>
              <p className="text-sm text-muted-foreground mt-2 text-center">Al comprar {r.min_qty}+ artículos</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editItem ? 'Editar Regla' : 'Nueva Regla de Descuento'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Nombre</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Descuento por volumen" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Cantidad Mínima</Label><Input type="number" min="1" value={form.min_qty} onChange={e => setForm({...form, min_qty: parseInt(e.target.value) || 1})} /></div>
              <div className="space-y-2"><Label>% Descuento</Label><Input type="number" step="0.1" min="0" max="100" value={form.discount_percent} onChange={e => setForm({...form, discount_percent: parseFloat(e.target.value) || 0})} /></div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.active} onCheckedChange={v => setForm({...form, active: v})} />
              <Label>Activa</Label>
            </div>
            <div className="bg-secondary/50 rounded-md p-3 text-sm">
              <strong>Vista previa:</strong> Si el cliente compra {form.min_qty}+ artículos, obtiene {form.discount_percent}% de descuento en el subtotal.
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleSave}>{editItem ? 'Actualizar' : 'Crear'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
